import { BUSINESS_CATEGORY_ID } from "../constants/const";
import { MOCK_MY_PROFILE_IS_DEAD } from "../constants/deadAccount";
import { ItemCategory, ProfilePlanType } from "../constants/enum/enum";
import {
  menLinksOriginal,
  menLinksSmall,
  womenLinksOriginal,
  womenLinksSmall,
} from "../constants/avatarLinks";
import { InventoryItemInfos } from "../constants/inventoryItemInfo";
import { InventoryItemInfo } from "../types/InventoryItemInfo";
import InventorySlotInfo from "../types/InventorySlotInfo";
import { ItemInfo } from "../types/ItemInfo";
import { UserProfile } from "../types/UserProfile";

export const DEFAULT_PROFILE_AVATAR =
  "/assets/imgs/profile/default-avatar.png";

export const getProfileAvatarUrl = (
  profile?: Partial<UserProfile> | null,
  size: "small" | "original" = "small"
): string => {
  if (!profile) {
    return DEFAULT_PROFILE_AVATAR;
  }

  if (
    (profile.premium === ProfilePlanType.Plus ||
      profile.premium === ProfilePlanType.Unlimited) &&
    profile.customAvatar
  ) {
    return profile.customAvatar;
  }

  const menLinks = size === "original" ? menLinksOriginal : menLinksSmall;
  const womenLinks = size === "original" ? womenLinksOriginal : womenLinksSmall;
  const imageIndex = profile.imageId ? profile.imageId : -1;

  if (profile.imageId !== undefined && profile.imageId !== null) {
    return profile.gender === 1
      ? womenLinks[imageIndex] || DEFAULT_PROFILE_AVATAR
      : menLinks[imageIndex] || DEFAULT_PROFILE_AVATAR;
  }

  return DEFAULT_PROFILE_AVATAR;
};

/** Normalize is_dead / isDead from API (avoids `isDead: false` masking `is_dead: true`). */
export const parseProfileIsDead = (
  profile: Partial<UserProfile> | Record<string, unknown> | null | undefined
): boolean => {
  if (!profile) return false;

  const record = profile as Record<string, unknown>;
  const hasSnake = record.is_dead !== undefined && record.is_dead !== null;
  const raw = hasSnake ? record.is_dead : record.isDead;

  if (raw === true || raw === 1) return true;
  if (raw === false || raw === 0 || raw === null || raw === undefined) {
    return false;
  }
  if (typeof raw === "string") {
    const normalized = raw.trim().toLowerCase();
    if (normalized === "true" || normalized === "1" || normalized === "yes") {
      return true;
    }
    if (normalized === "false" || normalized === "0" || normalized === "no") {
      return false;
    }
  }

  return Boolean(raw);
};

/** Dead state for the logged-in player's profile (includes dev mock). */
export const resolveMyProfileIsDead = (
  profile: Partial<UserProfile> | null | undefined
): boolean => {
  if (!profile?.name && !profile?.id) return false;
  if (MOCK_MY_PROFILE_IS_DEAD) return true;
  return parseProfileIsDead(profile);
};
export const parseOTCOfferItems = (payload: any) => {
  return payload.map((item: any) => {
    return {
      ...item,
      offerItemDataList: item.offerItemDataList.map((itemData: any) => {
        return {
          ...itemData,
          cityId: itemData.city,
          x: itemData.Map?.slotX,
          y: itemData.Map?.slotY,
          mapCityId: itemData.Map?.city,
          slotSubType: itemData.Map?.slotSubType,
        };
      }),
      requestItems: item.requestItems.map((item: any) => {
        return {
          ...item,
          cityId:
            item.categoryId === BUSINESS_CATEGORY_ID ||
            item.categoryId === ItemCategory.LANDSLOT
              ? item.cityId
              : -1,
          mapCityId: item.Map?.city,
          slotSubType: item.Map?.slotSubType,
        };
      }),
    };
  });
};

export const parseInventoryItems = (payload: any) => {
  return {
    inventoryItems: payload.inventoryData.map((item: any, index: number) => {
      return {
        itemId: item.id,
        categoryId: item.itemDetail.category_id,
        typeId: item.itemDetail.type_id,
        cityId: item.city,
        inventoryId: item.item_id,
        typeName: item.itemDetail.type_name,
      };
    }),
    slotItems: payload.slotData.map((item: any, index: number) => {
      return item as InventorySlotInfo;
    }),
  };
};

export const hasAllItems = (
  myInventoryItems: ItemInfo[],
  itemInfos: ItemInfo[],
  mySlotItems: InventorySlotInfo[]
): [boolean, number[]] => {
  // Create a count map for itemInfos
  const itemCount: Record<string, number> = {};

  // Count occurrences of each item in itemInfos
  for (const item of itemInfos) {
    // if type of ognft and credit is from 0 to 6, format as 0
    let typeId = item.typeId;
    if (
      (item.categoryId === 11 || item.categoryId === 12) &&
      item.typeId >= 0 &&
      item.typeId < 6
    ) {
      typeId = 0;
    }

    const key =
      item.categoryId === ItemCategory.LANDSLOT
        ? `${item.cityId}-${item.slotX}-${item.slotY}`
        : `${item.categoryId}-${typeId}-${item.cityId}`;
    itemCount[key] = (itemCount[key] || 0) + 1;
  }

  // Array to hold matching itemIds
  const matchingItemIds: number[] = [];
  const inventoryItemsCopy = [...myInventoryItems];

  for (const item of itemInfos) {
    let typeId = item.typeId;
    if (
      (item.categoryId === 11 || item.categoryId === 12) &&
      item.typeId >= 0 &&
      item.typeId < 6
    ) {
      typeId = 0;
    }

    const key =
      item.categoryId === ItemCategory.LANDSLOT
        ? `${item.cityId}-${item.slotX}-${item.slotY}`
        : `${item.categoryId}-${typeId}-${item.cityId}`;

    const index = inventoryItemsCopy.findIndex((invItem) => {
      let invTypeId = invItem.typeId;
      if (
        (invItem.categoryId === 11 || invItem.categoryId === 12) &&
        invItem.typeId >= 0 &&
        invItem.typeId < 6
      ) {
        invTypeId = 0;
      }

      const slotData = mySlotItems.find(
        (it) => it.inventoryItemId === invItem.itemId
      );
      const invKey =
        invItem.categoryId === ItemCategory.LANDSLOT
          ? `${slotData?.city}-${slotData?.slotX}-${slotData?.slotY}`
          : `${invItem.categoryId}-${invTypeId}-${invItem.cityId}`;

      return key === invKey;
    });
    if (index !== -1) {
      const matchedItem = inventoryItemsCopy[index];
      matchingItemIds.push(matchedItem.itemId);
      inventoryItemsCopy.splice(index, 1);
    } else {
      return [false, []];
    }
  }
  return [true, matchingItemIds];
};

export const parseInventoryItemInfos = (data: any) => {
  return data.map((item: any) => {
    return {
      itemId: item.id,
      categoryId: item.category_id,
      typeId: item.type_id,
      categoryName: item.category_name,
      typeName: item.type_name,
      chance1: item.chance1,
      chance2: item.chance2,
      inventoryWorth: item.inventory_worth,
    };
  }) as InventoryItemInfo[];
};

export const getInventoryItemInfoById = (itemId: number) => {
  return InventoryItemInfos.find((item) => item.itemId === itemId);
};

export const getItemIdWithCategoryAndType = (
  categoryId: number,
  typeId: number
) => {
  return InventoryItemInfos.find(
    (item) => item.categoryId === categoryId && item.typeId === typeId
  )?.itemId;
};
